import axios from "axios";
import { getApiBaseUrl } from "@/lib/config";
import { logApiFailure, logActionError } from "@/lib/errorLogging";

const API = getApiBaseUrl();

const SERVER_ASSISTED_DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024;
const SERVER_ASSISTED_RETRY_LIMIT = 4;
const DIRECT_UPLOAD_RETRY_LIMIT = 3;
const DIRECT_UPLOAD_MULTIPART_CONCURRENCY = 2;
const DIRECT_UPLOAD_MAX_RETRY_LIMIT = 5;
const UPLOAD_API_RETRY_LIMIT = 4;
const DOCUMENT_UPLOAD_RETRY_LIMIT = 3;
const CLOUD_UPLOAD_STALL_TIMEOUT_MIN_MS = 10 * 60 * 1000;
const CLOUD_UPLOAD_MIN_TOTAL_TIMEOUT_MS = 8 * 60 * 1000;
const CLOUD_UPLOAD_MAX_TOTAL_TIMEOUT_MS = 4 * 60 * 60 * 1000;
const LARGE_MEDIA_DIRECT_UPLOAD_ONLY_THRESHOLD_BYTES = 100 * 1024 * 1024;
const SILENT_ERROR_LOG_CONFIG_KEY = "__voxaraSuppressErrorLog";
const UNAMBIGUOUS_AUDIO_EXTENSIONS = new Set([".mp3", ".wav", ".m4a", ".aac", ".ogg", ".flac"]);
const DOCUMENT_EXTENSIONS = new Set([".docx", ".pdf"]);
const DOCUMENT_MIME_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

const NETWORK_ERROR_MESSAGES = {
  es: "Se perdió la conexión temporalmente. Intenta de nuevo.",
  en: "The connection was temporarily lost. Please try again.",
};
const TRANSIENT_GET_RETRY_DELAY_MS = 700;
const PASSIVE_TRANSIENT_GET_ENDPOINT_PATTERNS = [
  "/auth/me",
  "/auth/session-idle-state",
  "/admin/stats",
  "/admin/costs",
  "/admin/system-capacity",
  "/admin/r2-status",
  "/admin/live-monitor",
  "/subscription/status",
  "/subscription/checkout-status/",
  "/videos",
  "/usage",
];

const getStoredLanguage = () => {
  if (typeof window === "undefined") {
    return "es";
  }

  const storedLanguage = window.localStorage.getItem("voxara_language");
  if (storedLanguage === "en" || storedLanguage === "es") {
    return storedLanguage;
  }

  return String(window.navigator?.language || "es").toLowerCase().startsWith("en") ? "en" : "es";
};

export const getLocalizedNetworkErrorMessage = (language = getStoredLanguage()) =>
  NETWORK_ERROR_MESSAGES[language] || NETWORK_ERROR_MESSAGES.es;

const getBrowserNetworkHints = () => {
  if (typeof navigator === "undefined") {
    return { effectiveType: "unknown", saveData: false };
  }

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return {
    effectiveType: String(connection?.effectiveType || "unknown").toLowerCase(),
    saveData: Boolean(connection?.saveData),
  };
};

const estimateUploadBytesPerSecond = (hints = getBrowserNetworkHints()) => {
  if (hints.saveData || hints.effectiveType === "slow-2g") return 48 * 1024;
  if (hints.effectiveType === "2g") return 64 * 1024;
  if (hints.effectiveType === "3g") return 96 * 1024;
  if (hints.effectiveType === "4g") return 512 * 1024;
  return 192 * 1024;
};

const getAdaptiveRetryDelayMs = (attempt) => {
  const cappedAttempt = Math.max(1, Math.min(attempt, 6));
  const baseDelay = Math.min(16_000, 1000 * (2 ** (cappedAttempt - 1)));
  const jitter = Math.floor(Math.random() * 500);
  return baseDelay + jitter;
};

const getAdaptiveMultipartConcurrency = (file) => {
  const hints = getBrowserNetworkHints();
  const fileSize = Number(file?.size || 0);
  const isVeryLargeFile = fileSize >= 450 * 1024 * 1024;

  if (hints.saveData || hints.effectiveType === "slow-2g" || hints.effectiveType === "2g") {
    return 1;
  }
  if (hints.effectiveType === "3g") {
    return isVeryLargeFile || fileSize >= 350 * 1024 * 1024 ? 1 : 2;
  }
  if (hints.effectiveType === "4g" && fileSize <= 120 * 1024 * 1024) {
    return 4;
  }
  if (hints.effectiveType === "4g") {
    return isVeryLargeFile ? 2 : 3;
  }
  if (fileSize <= 180 * 1024 * 1024) {
    return 4;
  }
  if (isVeryLargeFile) {
    return 2;
  }
  return DIRECT_UPLOAD_MULTIPART_CONCURRENCY;
};

const getAdaptiveUploadRetryLimit = (file) => {
  const hints = getBrowserNetworkHints();
  const fileSize = Number(file?.size || 0);

  if (
    hints.saveData ||
    hints.effectiveType === "slow-2g" ||
    hints.effectiveType === "2g"
  ) {
    return DIRECT_UPLOAD_MAX_RETRY_LIMIT;
  }
  if (hints.effectiveType === "3g" || fileSize >= 350 * 1024 * 1024) {
    return Math.min(DIRECT_UPLOAD_MAX_RETRY_LIMIT, DIRECT_UPLOAD_RETRY_LIMIT + 2);
  }
  if (fileSize >= 100 * 1024 * 1024) {
    return Math.min(DIRECT_UPLOAD_MAX_RETRY_LIMIT, DIRECT_UPLOAD_RETRY_LIMIT + 1);
  }
  return DIRECT_UPLOAD_RETRY_LIMIT;
};

const isRetryableUploadError = (error) => {
  const status = error?.response?.status || 0;
  const message = String(error?.response?.data?.detail || error?.message || "").toLowerCase();

  if (status === 0 || status === 408 || status === 429 || status >= 500) {
    return true;
  }
  if (status === 400 || status === 403 || status === 404) {
    return false;
  }
  return /(network error|failed to fetch|networkerror|load failed|timeout|connection|offline|abort|stalled)/i.test(message);
};

const requestWithUploadRetry = async (requestFactory, options = {}) => {
  const maxAttempts = Math.max(1, Number(options.maxAttempts || UPLOAD_API_RETRY_LIMIT));
  const shouldRetry = options.shouldRetry || isRetryableUploadError;

  let attempt = 0;
  while (attempt < maxAttempts) {
    attempt += 1;
    try {
      return await requestFactory(attempt);
    } catch (error) {
      if (!shouldRetry(error) || attempt >= maxAttempts) {
        throw error;
      }
      await wait(getAdaptiveRetryDelayMs(attempt));
    }
  }

  throw new Error("Upload request exhausted retries");
};

export const isNetworkConnectionError = (error) => {
  const status = error?.response?.status || 0;
  const rawMessage = String(error?.response?.data?.detail || error?.message || "").toLowerCase();
  return status === 0 || /(network error|failed to fetch|networkerror|load failed|timeout|connection|offline|abort)/i.test(rawMessage);
};

export const getApiErrorMessage = (error, fallbackMessage, language = getStoredLanguage()) => {
  if (isNetworkConnectionError(error)) {
    return getLocalizedNetworkErrorMessage(language);
  }
  return error?.response?.data?.detail || error?.userFriendlyMessage || error?.message || fallbackMessage;
};

const shouldRetryTransientGetRequest = (error) => {
  const method = String(error?.config?.method || "").toLowerCase();
  if (method !== "get") return false;
  if (!isNetworkConnectionError(error)) return false;
  if (error?.config?.__voxaraTransientRetried) return false;
  const normalizedUrl = String(error?.config?.url || "").toLowerCase();
  if (normalizedUrl.includes("/client-error-logs")) return false;
  return true;
};

const isPassiveTransientGetNetworkError = (method, normalizedUrl, error) => {
  if (method !== "GET") return false;
  if (!isNetworkConnectionError(error)) return false;
  return PASSIVE_TRANSIENT_GET_ENDPOINT_PATTERNS.some((pattern) => normalizedUrl.includes(pattern));
};

const detectAudioFile = (file) => {
  const mimeType = (file?.type || "").toLowerCase();
  const fileName = (file?.name || "").toLowerCase();
  const dotIndex = fileName.lastIndexOf(".");
  const extension = dotIndex >= 0 ? fileName.slice(dotIndex) : "";

  if (mimeType.startsWith("audio/")) {
    return true;
  }

  if (mimeType.startsWith("video/")) {
    return false;
  }

  return UNAMBIGUOUS_AUDIO_EXTENSIONS.has(extension);
};

const detectDocumentFile = (file) => {
  const mimeType = (file?.type || "").toLowerCase();
  const fileName = (file?.name || "").toLowerCase();
  const dotIndex = fileName.lastIndexOf(".");
  const extension = dotIndex >= 0 ? fileName.slice(dotIndex) : "";

  return DOCUMENT_MIME_TYPES.has(mimeType) || DOCUMENT_EXTENSIONS.has(extension);
};

const normalizeSpeakerConfig = (speakerConfig) => {
  if (!speakerConfig || typeof speakerConfig !== "object") {
    return {
      num_speakers: null,
      min_speakers: 1,
      max_speakers: 10,
      expected_speaker_count: null,
    };
  }

  const parseCount = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsedValue = Number.parseInt(String(value), 10);
    if (!Number.isFinite(parsedValue) || parsedValue < 1) return null;
    return Math.min(20, parsedValue);
  };

  const normalizedNumSpeakers = parseCount(
    speakerConfig.numSpeakers ?? speakerConfig.num_speakers
  );
  if (normalizedNumSpeakers !== null) {
    return {
      num_speakers: normalizedNumSpeakers,
      min_speakers: normalizedNumSpeakers,
      max_speakers: normalizedNumSpeakers,
      expected_speaker_count: normalizedNumSpeakers,
    };
  }

  const normalizedExpectedSpeakerCount = parseCount(
    speakerConfig.expectedSpeakerCount ?? speakerConfig.expected_speaker_count
  );
  const normalizedMinSpeakers = parseCount(
    speakerConfig.minSpeakers ?? speakerConfig.min_speakers
  ) ?? 1;
  const normalizedMaxSpeakers = parseCount(
    speakerConfig.maxSpeakers ?? speakerConfig.max_speakers
  ) ?? 10;
  return {
    num_speakers: null,
    min_speakers: Math.min(normalizedMinSpeakers, normalizedMaxSpeakers),
    max_speakers: Math.max(normalizedMinSpeakers, normalizedMaxSpeakers),
    expected_speaker_count: normalizedExpectedSpeakerCount,
  };
};

// Set auth header for axios
axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("voxara_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't show error for HEAD requests (used for resume check)
    if (error.config?.method === 'head') {
      return Promise.reject(error);
    }

    if (shouldRetryTransientGetRequest(error)) {
      return wait(TRANSIENT_GET_RETRY_DELAY_MS).then(() => axios.request({
        ...error.config,
        __voxaraTransientRetried: true,
      }));
    }

    const method = error.config?.method?.toUpperCase?.() || "GET";
    const url = error.config?.url || "";
    const status = error.response?.status || 0;
    let message = error.response?.data?.detail || error.message || "API request failed";
    const responseData = error.response?.data || null;

    if (isNetworkConnectionError(error)) {
      const localizedNetworkMessage = getLocalizedNetworkErrorMessage();
      error.userFriendlyMessage = localizedNetworkMessage;
      error.message = localizedNetworkMessage;
      if (!error.response) {
        error.response = { status: 0, data: {} };
      }
      if (!error.response.data) {
        error.response.data = {};
      }
      error.response.data.detail = localizedNetworkMessage;
      message = localizedNetworkMessage;
    }

    const normalizedMessage = String(message || "").toLowerCase();
    const normalizedUrl = String(url || "").toLowerCase();
    const isTransientAuthMeNetworkError = normalizedUrl.includes("/auth/me") && isNetworkConnectionError(error);
    const isSilentPassiveNetworkError = isPassiveTransientGetNetworkError(method, normalizedUrl, error);
    const shouldSuppressLog = Boolean(error.config?.[SILENT_ERROR_LOG_CONFIG_KEY]);
    const isAuthRequest = [
      "/auth/login",
      "/auth/verify-mfa",
      "/auth/resend-mfa",
      "/auth/register",
      "/auth/request-reset",
      "/auth/reset-password",
    ].some((segment) => normalizedUrl.includes(segment));

    if (
      typeof window !== "undefined" &&
      !isAuthRequest &&
      (status === 401 || status === 403) &&
      /(session invalidated|newer login|token expired|not authenticated|could not validate credentials|invalid token|unauthorized|forbidden)/i.test(normalizedMessage)
    ) {
      window.dispatchEvent(
        new CustomEvent("voxara-auth-invalidated", {
          detail: {
            reason: normalizedMessage.includes("idle") ? "idle" : "session-conflict",
            status,
            url,
            message,
          },
        })
      );
    }

    if (!isTransientAuthMeNetworkError && !isSilentPassiveNetworkError && !shouldSuppressLog) {
      logApiFailure({ method, url, status, message, responseData });
    }
    console.error("API Error:", error.response?.status || error.message);
    return Promise.reject(error);
  }
);

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const shouldPreferDirectR2UploadForMedia = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return true;
};

const shouldFallbackToServerAssistedUpload = (error, file = null) => {
  const fileSize = Number(file?.size || 0);
  if (fileSize >= LARGE_MEDIA_DIRECT_UPLOAD_ONLY_THRESHOLD_BYTES) {
    return false;
  }
  const message = String(error?.message || "").toLowerCase();
  return (
    message.includes("cloud upload") ||
    message.includes("subida directa a cloud") ||
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("load failed") ||
    message.includes("cors")
  );
};

const getCloudUploadTotalTimeoutMs = (blobSize = 0) => {
  const estimatedAtSlowConnectionMs = Math.ceil((Math.max(blobSize, 1) / estimateUploadBytesPerSecond()) * 1000);
  return Math.min(
    CLOUD_UPLOAD_MAX_TOTAL_TIMEOUT_MS,
    Math.max(CLOUD_UPLOAD_MIN_TOTAL_TIMEOUT_MS, estimatedAtSlowConnectionMs + 2 * 60 * 1000)
  );
};

const getCloudUploadStallTimeoutMs = (blobSize = 0) => {
  const totalTimeoutMs = getCloudUploadTotalTimeoutMs(blobSize);
  const estimatedUploadMs = Math.ceil((Math.max(blobSize, 1) / estimateUploadBytesPerSecond()) * 1000);
  return Math.min(
    Math.max(30_000, totalTimeoutMs - 15_000),
    Math.max(CLOUD_UPLOAD_STALL_TIMEOUT_MIN_MS, estimatedUploadMs + 60_000)
  );
};

const uploadBlobToPresignedUrl = (uploadUrl, blob, contentType, onProgress) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  let settled = false;
  let stallTimer = null;

  const finish = (callback) => {
    if (settled) return;
    settled = true;
    if (stallTimer) {
      clearTimeout(stallTimer);
      stallTimer = null;
    }
    callback();
  };

  const resetStallTimer = () => {
    if (stallTimer) {
      clearTimeout(stallTimer);
    }
    stallTimer = setTimeout(() => {
      try {
        xhr.abort();
      } catch (_error) {
        // no-op
      }
      finish(() => reject(new Error("Cloud upload stalled")));
    }, getCloudUploadStallTimeoutMs(blob?.size || 0));
  };

  xhr.open("PUT", uploadUrl, true);
  xhr.timeout = getCloudUploadTotalTimeoutMs(blob?.size || 0);
  if (contentType) {
    xhr.setRequestHeader("Content-Type", contentType);
  }
  resetStallTimer();
  xhr.upload.onprogress = (event) => {
    if (!onProgress) return;
    if (!event.lengthComputable) return;
    resetStallTimer();
    onProgress(event.loaded, event.total || blob.size || 1);
  };
  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) {
      finish(() => resolve({
        etag: xhr.getResponseHeader("ETag") || xhr.getResponseHeader("etag") || null,
      }));
      return;
    }
    finish(() => reject(new Error(`Cloud upload failed with status ${xhr.status}`)));
  };
  xhr.onerror = () => finish(() => reject(new Error("Cloud upload failed")));
  xhr.ontimeout = () => finish(() => reject(new Error("Cloud upload timed out")));
  xhr.onabort = () => finish(() => reject(new Error("Cloud upload aborted")));
  xhr.send(blob);
});

const parseUploadOffset = (headers, fallbackOffset = 0) => {
  const rawOffset = headers?.["upload-offset"] ?? headers?.["Upload-Offset"];
  const parsedOffset = Number(rawOffset);
  return Number.isFinite(parsedOffset) && parsedOffset >= 0 ? parsedOffset : fallbackOffset;
};

const getServerUploadOffset = async (uploadId) => {
  const response = await axios.head(`${API}/upload/${uploadId}`);
  return parseUploadOffset(response.headers, 0);
};

const sendServerUploadChunk = async ({ uploadId, offset, chunk, fileSize, onProgress }) => {
  const response = await axios.patch(`${API}/upload/${uploadId}`, chunk, {
    headers: {
      "Content-Type": "application/offset+octet-stream",
      "Upload-Offset": String(offset),
    },
    onUploadProgress: (event) => {
      if (!onProgress) {
        return;
      }
      const loaded = Math.min(chunk.size, event?.loaded || 0);
      const totalUploaded = Math.min(fileSize, offset + loaded);
      onProgress(Math.round((totalUploaded / fileSize) * 90));
    },
  });

  return parseUploadOffset(response.headers, offset + chunk.size);
};

const normalizeTestTranscriptionEngine = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "camino1" || normalized === "camino2" ? normalized : null;
};

const serverAssistedUpload = async (file, language, onProgress, onStatus, speakerConfig = null, testTranscriptionEngine = null, recordingType = null, isLiveRecording = false) => {
  const normalizedSpeakerConfig = normalizeSpeakerConfig(speakerConfig);
  const normalizedTestTranscriptionEngine = normalizeTestTranscriptionEngine(testTranscriptionEngine);
  const initResponse = await requestWithUploadRetry(() => axios.post(`${API}/upload/init`, {
    filename: file.name,
    file_size: file.size,
    content_type: file.type || "application/octet-stream",
    language,
    ...(recordingType ? { recording_type: recordingType } : {}),
    ...(isLiveRecording ? { is_live_recording: true } : {}),
    is_audio: detectAudioFile(file),
    ...normalizedSpeakerConfig,
    ...(normalizedTestTranscriptionEngine ? { test_transcription_engine: normalizedTestTranscriptionEngine } : {}),
  }), { maxAttempts: UPLOAD_API_RETRY_LIMIT });

  const session = initResponse.data;
  const chunkSize = Number(session.chunk_size) || SERVER_ASSISTED_DEFAULT_CHUNK_SIZE;
  let offset = Number(session.upload_offset) || 0;

  try {
    if (onStatus) onStatus("uploading");
    if (onProgress) onProgress(Math.round((offset / file.size) * 90));

    while (offset < file.size) {
      const chunk = file.slice(offset, Math.min(offset + chunkSize, file.size));
      let attempt = 0;
      let chunkUploaded = false;

      while (!chunkUploaded && attempt < SERVER_ASSISTED_RETRY_LIMIT) {
        attempt += 1;
        try {
          offset = await sendServerUploadChunk({
            uploadId: session.upload_id,
            offset,
            chunk,
            fileSize: file.size,
            onProgress,
          });
          if (onProgress) {
            onProgress(Math.round((offset / file.size) * 90));
          }
          chunkUploaded = true;
        } catch (error) {
          let resumedOffset = offset;

          try {
            resumedOffset = await getServerUploadOffset(session.upload_id);
          } catch (_resumeError) {
            resumedOffset = offset;
          }

          if (resumedOffset > offset) {
            offset = resumedOffset;
            if (onProgress) {
              onProgress(Math.round((offset / file.size) * 90));
            }
            chunkUploaded = true;
            break;
          }

          if (error?.response?.status === 409) {
            offset = resumedOffset;
          }

          if (attempt >= SERVER_ASSISTED_RETRY_LIMIT) {
            throw error;
          }

          await wait(attempt * 900);
        }
      }
    }

    if (onStatus) onStatus("finalizing");
    if (onProgress) onProgress(95);

    const completeResponse = await requestWithUploadRetry(
      () => axios.post(`${API}/upload/${session.upload_id}/complete`),
      { maxAttempts: UPLOAD_API_RETRY_LIMIT }
    );
    if (onStatus) onStatus("processing");
    if (onProgress) onProgress(100);
    return completeResponse.data;
  } catch (error) {
    try {
      await axios.delete(`${API}/upload/${session.upload_id}`, {
        [SILENT_ERROR_LOG_CONFIG_KEY]: true,
        timeout: 8000,
      });
    } catch (_cleanupError) {
      // best effort cleanup
    }

    if (error instanceof TypeError || /Failed to fetch|NetworkError|Load failed|timeout/i.test(error?.message || "")) {
      throw new Error("The upload connection was interrupted. Please retry — Voxara will safely restart the server-assisted upload.");
    }

    throw error;
  }
};

const directR2Upload = async (file, language, onProgress, onStatus, speakerConfig = null, testTranscriptionEngine = null, contentKind = "media", recordingType = null, isLiveRecording = false) => {
  const normalizedSpeakerConfig = normalizeSpeakerConfig(speakerConfig);
  const normalizedTestTranscriptionEngine = normalizeTestTranscriptionEngine(testTranscriptionEngine);
  const normalizedContentKind = contentKind === "document" ? "document" : "media";
  const multipartConcurrency = getAdaptiveMultipartConcurrency(file);
  const uploadRetryLimit = getAdaptiveUploadRetryLimit(file);
  let lastEmittedProgress = 0;
  const emitProgress = (value) => {
    if (!onProgress) return;
    const boundedValue = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
    lastEmittedProgress = Math.max(lastEmittedProgress, boundedValue);
    onProgress(lastEmittedProgress);
  };
  const initResponse = await requestWithUploadRetry(() => axios.post(`${API}/upload/direct/init`, {
    filename: file.name,
    file_size: file.size,
    content_type: file.type || "application/octet-stream",
    language,
    ...(recordingType ? { recording_type: recordingType } : {}),
    ...(isLiveRecording ? { is_live_recording: true } : {}),
    is_audio: detectAudioFile(file),
    content_kind: normalizedContentKind,
    ...normalizedSpeakerConfig,
    ...(normalizedTestTranscriptionEngine ? { test_transcription_engine: normalizedTestTranscriptionEngine } : {}),
  }), { maxAttempts: UPLOAD_API_RETRY_LIMIT });

  const session = initResponse.data;
  const contentType = file.type || "application/octet-stream";

  try {
    if (onStatus) onStatus("uploading");
    emitProgress(5);

    if (session.strategy === "put" && session.upload_url) {
      await uploadBlobToPresignedUrl(session.upload_url, file, contentType, (loaded, total) => {
        emitProgress(Math.min(95, Math.max(5, Math.round((loaded / Math.max(total || file.size || 1, 1)) * 95))));
      });
    } else {
      const partSize = Number(session.part_size) || 25 * 1024 * 1024;
      const totalParts = Number(session.total_parts) || Math.max(1, Math.ceil(file.size / partSize));
      let committedBytes = 0;
      const inFlightPartProgress = new Map();

      const emitMultipartProgress = () => {
        const inFlightBytes = Array.from(inFlightPartProgress.values()).reduce((sum, value) => sum + value, 0);
        const totalUploaded = Math.min(file.size, committedBytes + inFlightBytes);
        emitProgress(Math.min(95, Math.max(5, Math.round((totalUploaded / Math.max(file.size || 1, 1)) * 95))));
      };

      const uploadMultipartPart = async (partInfo) => {
        const partNumber = Number(partInfo.part_number);
        const partStart = (partNumber - 1) * partSize;
        const partEnd = Math.min(partStart + partSize, file.size);
        const partBlob = file.slice(partStart, partEnd);
        let attempt = 0;

        while (attempt < uploadRetryLimit) {
          attempt += 1;
          try {
            await uploadBlobToPresignedUrl(partInfo.upload_url, partBlob, contentType, (loaded) => {
              inFlightPartProgress.set(partNumber, Math.min(partBlob.size, loaded));
              emitMultipartProgress();
            });
            committedBytes += partBlob.size;
            inFlightPartProgress.delete(partNumber);
            emitMultipartProgress();
            return;
          } catch (error) {
            inFlightPartProgress.delete(partNumber);
            emitMultipartProgress();
            if (!isRetryableUploadError(error) || attempt >= uploadRetryLimit) {
              throw error;
            }
            await wait(getAdaptiveRetryDelayMs(attempt));
          }
        }
      };

      for (let startPartNumber = 1; startPartNumber <= totalParts; startPartNumber += Number(session.part_url_batch_size) || 16) {
        const partUrlsResponse = await requestWithUploadRetry(
          () => axios.post(`${API}/upload/direct/${session.upload_id}/part-urls`, {
            start_part_number: startPartNumber,
            count: Number(session.part_url_batch_size) || 16,
          }),
          { maxAttempts: UPLOAD_API_RETRY_LIMIT }
        );

        const partUrls = partUrlsResponse.data.upload_urls || [];
        for (let index = 0; index < partUrls.length; index += multipartConcurrency) {
          const group = partUrls.slice(index, index + multipartConcurrency);
          await Promise.all(group.map((partInfo) => uploadMultipartPart(partInfo)));
        }
      }
    }

    if (onStatus) onStatus("finalizing");
    emitProgress(97);

    const completeResponse = await requestWithUploadRetry(
      () => axios.post(`${API}/upload/direct/${session.upload_id}/complete`, {
        language,
        ...normalizedSpeakerConfig,
      }),
      { maxAttempts: UPLOAD_API_RETRY_LIMIT }
    );
    if (onStatus) onStatus("processing");
    emitProgress(100);
    return completeResponse.data;
  } catch (error) {
    try {
      await axios.delete(`${API}/upload/direct/${session.upload_id}`, {
        [SILENT_ERROR_LOG_CONFIG_KEY]: true,
        timeout: 8000,
      });
    } catch (_cleanupError) {
      // best effort cleanup
    }

    if (error instanceof TypeError || /Failed to fetch|NetworkError|Load failed|timeout|Cloud upload failed|Cloud upload stalled|Cloud upload aborted/i.test(error?.message || "")) {
      throw new Error("La subida directa a cloud se interrumpió o quedó sin respuesta. Voxara agotó varios reintentos automáticos para recuperar la carga. Revisa la conexión e inténtalo nuevamente.");
    }

    throw error;
  }
};

const documentUpload = async (file, onProgress, onStatus) => {
  const formData = new FormData();
  formData.append("file", file);

  if (onStatus) onStatus("uploading");
  if (onProgress) onProgress(8);

  const response = await requestWithUploadRetry(
    () => axios.post(`${API}/documents/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: getCloudUploadTotalTimeoutMs(file.size),
      onUploadProgress: (event) => {
        if (!onProgress) return;
        const total = event?.total || file.size || 1;
        const loaded = event?.loaded || 0;
        onProgress(Math.min(95, Math.max(10, Math.round((loaded / total) * 100))));
      },
    }),
    { maxAttempts: DOCUMENT_UPLOAD_RETRY_LIMIT }
  );

  if (onStatus) onStatus("processing");
  if (onProgress) onProgress(100);
  return response.data;
};

export const videoApi = {
  /**
   * Upload media using the server-assisted resumable flow
   */
  upload: async (file, language = "auto", onProgress, onStatus, options = {}) => {
    const isDocument = detectDocumentFile(file);
    const speakerConfig = isDocument ? null : normalizeSpeakerConfig(options.speakerConfig);
    const testTranscriptionEngine = isDocument ? null : normalizeTestTranscriptionEngine(options.testTranscriptionEngine);
    const recordingType = isDocument ? null : (options.recordingType || null);
    const isLiveRecording = !isDocument && Boolean(options.isLiveRecording);
    let result;

    if (isDocument) {
      try {
        result = await directR2Upload(file, language, onProgress, onStatus, null, null, "document");
      } catch (error) {
        console.warn("Direct cloud upload for document failed; falling back to classic document upload", error);
        if (onStatus) onStatus("recovering");
        if (onProgress) onProgress(5);
        result = await documentUpload(file, onProgress, onStatus);
      }
    } else if (shouldPreferDirectR2UploadForMedia()) {
      try {
        result = await directR2Upload(file, language, onProgress, onStatus, speakerConfig, testTranscriptionEngine, "media", recordingType, isLiveRecording);
      } catch (error) {
        if (!shouldFallbackToServerAssistedUpload(error, file)) {
          throw error;
        }

        // Surface why the direct path failed — without this the only trace is the fallback's
        // own error, which hides the real cause (CORS, a 4xx from R2, a stall, ...).
        console.warn("Direct cloud upload failed; falling back to server-assisted upload", error);
        try {
          logActionError({
            action: "direct_upload_failed",
            error,
            context: { fileName: file?.name, fileType: file?.type, fileSize: file?.size },
          });
        } catch (_e) { /* reporting must never break the upload */ }
        if (onStatus) onStatus("recovering");
        if (onProgress) onProgress(5);
        result = await serverAssistedUpload(file, language, onProgress, onStatus, speakerConfig, testTranscriptionEngine, recordingType, isLiveRecording);
      }
    } else {
      result = await serverAssistedUpload(file, language, onProgress, onStatus, speakerConfig, testTranscriptionEngine, recordingType, isLiveRecording);
    }

    return {
      video_id: result.video_id,
      job_id: result.job_id,
      status: "processing",
      content_kind: isDocument ? "document" : (detectAudioFile(file) ? "audio" : "video"),
      num_speakers: speakerConfig?.num_speakers ?? null,
      min_speakers: speakerConfig?.min_speakers ?? null,
      max_speakers: speakerConfig?.max_speakers ?? null,
      test_transcription_engine: testTranscriptionEngine,
    };
  },

  /**
   * Get job status
   */
  getJobStatus: async (jobId) => {
    const response = await axios.get(`${API}/job/${jobId}/status`);
    return response.data;
  },

  /**
   * List user's videos
   */
  list: async (skip = 0, limit = 20) => {
    const response = await axios.get(`${API}/videos`, {
      params: { skip, limit },
    });
    return response.data;
  },

  /**
   * Get single video details
   */
  get: async (videoId) => {
    const response = await axios.get(`${API}/videos/${videoId}`);
    return response.data;
  },

  /**
   * Delete video
   */
  delete: async (videoId) => {
    try {
      const response = await axios.delete(`${API}/videos/${videoId}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return {
          message: "Video already deleted",
          already_deleted: true,
        };
      }
      throw error;
    }
  },

  /**
   * Cancel processing video
   */
  cancel: async (videoId) => {
    const response = await axios.post(`${API}/videos/${videoId}/cancel`);
    return response.data;
  },

  /**
   * Export transcript
   */
  export: async (videoId, format = "txt", language = "en") => {
    const response = await axios.get(`${API}/videos/${videoId}/export`, {
      params: { format, language },
    });
    return response.data;
  },

  /**
   * Generate AI summary
   */
  summarize: async (videoId, language = "en") => {
    const response = await axios.post(`${API}/videos/${videoId}/summarize?language=${language}`);
    return response.data;
  },

  /**
   * Translate key moments to specified language
   */
  translateMoments: async (videoId, language = "en") => {
    const response = await axios.post(`${API}/videos/${videoId}/translate-moments?language=${language}`);
    return response.data;
  },

  /**
   * Translate summary to specified language
   */
  translateSummary: async (videoId, language = "en") => {
    const response = await axios.post(`${API}/videos/${videoId}/translate-summary?language=${language}`);
    return response.data;
  },

  /**
   * Translate transcript to specified language
   */
  translateTranscript: async (videoId, language = "en") => {
    const response = await axios.post(`${API}/videos/${videoId}/translate-transcript?language=${language}`);
    return response.data;
  },

  analyzeSpeakers: async (videoId, options = {}) => {
    const response = await axios.post(`${API}/videos/${videoId}/analyze-speakers`, null, {
      params: { force: options.force === true },
    });
    return response.data;
  },

  renameSpeaker: async (videoId, speakerId, payload) => {
    const response = await axios.patch(`${API}/videos/${videoId}/speakers/${speakerId}`, payload);
    return response.data;
  },

  deleteSpeaker: async (videoId, speakerId) => {
    const response = await axios.delete(`${API}/videos/${videoId}/speakers/${speakerId}`);
    return response.data;
  },

  mergeSpeakers: async (videoId, payload) => {
    const response = await axios.post(`${API}/videos/${videoId}/speakers/merge`, payload);
    return response.data;
  },

  updateSegmentSpeaker: async (videoId, segmentIndex, payload) => {
    const response = await axios.patch(`${API}/videos/${videoId}/segments/${segmentIndex}/speaker`, payload);
    return response.data;
  },

  updateSpeakerRange: async (videoId, payload) => {
    const response = await axios.patch(`${API}/videos/${videoId}/speaker-ranges`, payload);
    return response.data;
  },

  updateTranscript: async (videoId, payload) => {
    const response = await axios.patch(`${API}/videos/${videoId}/transcript`, payload);
    return response.data;
  },

  /**
   * Get user usage stats
   */
  getUsage: async () => {
    const response = await axios.get(`${API}/usage`);
    return response.data;
  },

  /**
   * Get admin cost statistics
   */
  getAdminCosts: async () => {
    const response = await axios.get(`${API}/admin/costs`);
    return response.data;
  },

  reportIssue: async (payload) => {
    const response = await axios.post(`${API}/report-issue`, payload);
    return response.data;
  },

  getVideoReview: async (videoId) => {
    const response = await axios.get(`${API}/video-reviews/${videoId}`);
    return response.data;
  },

  submitVideoReview: async (payload) => {
    const response = await axios.post(`${API}/video-reviews`, payload);
    return response.data;
  },

  getAdminIssueReports: async () => {
    const response = await axios.get(`${API}/admin/issue-reports`);
    return response.data;
  },

  deleteAdminIssueReport: async (reportId) => {
    const response = await axios.delete(`${API}/admin/issue-reports/${reportId}`);
    return response.data;
  },

  getAdminReviews: async () => {
    const response = await axios.get(`${API}/admin/reviews`);
    return response.data;
  },

  deleteAdminReview: async (reviewId) => {
    const response = await axios.delete(`${API}/admin/reviews/${reviewId}`);
    return response.data;
  },

  getAdminErrorLogs: async (params = {}) => {
    const response = await axios.get(`${API}/admin/error-logs`, { params });
    return response.data;
  },

  markAdminErrorLogsSeen: async (payload) => {
    const response = await axios.post(`${API}/admin/error-logs/mark-seen`, payload);
    return response.data;
  },

  deleteAdminErrorLogs: async (payload) => {
    const response = await axios.post(`${API}/admin/error-logs/delete`, payload);
    return response.data;
  },
};

export default videoApi;
